import { useCallback, useEffect, useRef } from "react"

import { isWeb } from "shared/lib/runtimeEnvironment"

export interface UseWindowFocusOptions {
  onFocus?: () => void
  onIdle?: () => void
  onResume?: () => void
  idleTimeout?: number // in milliseconds
}

export function useWindowFocus(fn: () => void): void
export function useWindowFocus(options: UseWindowFocusOptions): void
export function useWindowFocus(
  fnOrOptions: (() => void) | UseWindowFocusOptions
): void {
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isIdleRef = useRef(false)

  // Handle both function and options parameter
  const options: UseWindowFocusOptions =
    typeof fnOrOptions === "function" ? { onFocus: fnOrOptions } : fnOrOptions

  const { onFocus, onIdle, onResume, idleTimeout = 300000 } = options // default 5 minutes

  const clearIdleTimeout = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current)
      idleTimeoutRef.current = null
    }
  }, [])

  const startIdleTimeout = useCallback(() => {
    if (!onIdle) return

    clearIdleTimeout()
    idleTimeoutRef.current = setTimeout(() => {
      isIdleRef.current = true
      onIdle()
    }, idleTimeout)
  }, [onIdle, idleTimeout, clearIdleTimeout])

  const handleFocus = useCallback(() => {
    clearIdleTimeout()

    if (isIdleRef.current && onResume) {
      // Window regained focus after being idle
      onResume()
      isIdleRef.current = false
    } else if (onFocus) {
      // Regular focus event
      onFocus()
    }

    // Start idle timeout again if onIdle is provided
    if (onIdle) {
      startIdleTimeout()
    }
  }, [onFocus, onResume, onIdle, clearIdleTimeout, startIdleTimeout])

  const handleBlur = useCallback(() => {
    // Start idle timeout when window loses focus
    if (onIdle) {
      startIdleTimeout()
    }
  }, [onIdle, startIdleTimeout])

  useEffect(() => {
    if (!isWeb()) return undefined

    window.addEventListener("focus", handleFocus)
    window.addEventListener("blur", handleBlur)

    return () => {
      window.removeEventListener("focus", handleFocus)
      window.removeEventListener("blur", handleBlur)
      clearIdleTimeout()
    }
  }, [handleFocus, handleBlur, onIdle, startIdleTimeout, clearIdleTimeout])
}
